import { Field as ChakraField, Fieldset, Input } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import ModifiedTag from '@components/common/form/ModifiedField';
import { GPSCoordinatesField } from '@components/ui/coordinatesInput';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
  GlobalScreenConfiguration,
  GlobalScreenConfigurationWithNullableCoordinates,
} from '@/types/config/screenConfiguration.types';

export function ScreenConfigInformationForm() {
  const { errors, touched, initialValues, values } =
    useFormikContext<Partial<GlobalScreenConfigurationWithNullableCoordinates>>();

  return (
    <CockpitFormLayout title="components.operatorStationConfiguration.context.general.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <Fieldset.Content>
          <ChakraField.Root variant="cockpit" required id="name" width="100%" invalid={touched.name && !!errors.name}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="screenConfiguration.configurationName" />
              <RequiredIndicator />
              {initialValues.name !== values.name && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} id="name" name="name" type="text" />
          </ChakraField.Root>
          <ChakraField.Root variant="cockpit" required id="position" width="100%">
            <ChakraField.Label>
              <FormattedMessage id="screenConfiguration.configurationPosition" />
              <RequiredIndicator />
              {!isEqual(initialValues.position, values.position) && <ModifiedTag />}
            </ChakraField.Label>
            <Fieldset.Root invalid={touched.position && !!errors.position}>
              <Fieldset.Content>
                <Field name="position">
                  {({ field }: FieldProps<GlobalScreenConfiguration['position']>) => {
                    const coords = {
                      latitude: field.value.latitude,
                      longitude: field.value.longitude,
                      type: '2D',
                    };
                    return <GPSCoordinatesField name="position" coordinates={coords} />;
                  }}
                </Field>
              </Fieldset.Content>
            </Fieldset.Root>
          </ChakraField.Root>
          {Object.keys(errors).some((key) => {
            const error = getIn(errors, key);
            const isTouched = getIn(touched, key);
            if (typeof error === 'object' && error !== null) {
              return Object.keys(error).some((subKey) => {
                return isTouched?.[subKey] && !!error?.[subKey];
              });
            }
            return isTouched && !!error;
          }) && (
            <Fieldset.ErrorText>
              <ErrorsContainer errors={errors} touched={touched} />
            </Fieldset.ErrorText>
          )}
        </Fieldset.Content>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
